import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import PortalIdParser from 'PortalIdParser';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Small } from 'foundations-components/transitional/components/Small';
import { useCallback, useState } from 'react';
import { useEnrollMutation } from '../../__generated__/chirp/com/hubspot/feature/rollouts/app/rpc/BetaAccessRolloutRpc.hooks';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getHubSpotBetaTermsUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ENROLL_TYPE = 'PORTALS_ONLY';
const JoinPublicBetaModal = ({
  agentType,
  rolloutId,
  title,
  onSuccessfulJoin,
  onClose,
  skipBetaAlert = false,
  agentHubBeta = false
}) => {
  const portalId = PortalIdParser.get();
  const {
    trackInteraction,
    userInfo,
    productType
  } = useDetailsPageContext();
  const [isTermsAcknowledged, setIsTermsAcknowledged] = useState(false);
  const [joinBeta, {
    loading,
    error
  }] = useEnrollMutation();
  const userId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.user;
  const handleSuccessfulJoin = useCallback(() => {
    trackInteraction({
      action: 'beta joined',
      section: 'join public beta modal'
    });
    if (!skipBetaAlert) {
      FloatingAlertStore.addAlert({
        type: 'success',
        message: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.accountJoined', {
          betaName: title
        })
      });
    }
    if (onSuccessfulJoin) {
      onSuccessfulJoin();
    }
  }, [onSuccessfulJoin, skipBetaAlert, title, trackInteraction]);
  const handleJoin = useCallback(() => {
    if (!portalId || !userId) {
      FloatingAlertStore.addAlert({
        type: 'error',
        message: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.problemJoiningAccount')
      });
      return;
    }
    joinBeta({
      variables: {
        rolloutId,
        enrollType: ENROLL_TYPE
      }
    }).then(() => {
      handleSuccessfulJoin();
    }).catch(() => {
      FloatingAlertStore.addAlert({
        type: 'error',
        message: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.problemJoiningAccount')
      });
    }).finally(onClose);
  }, [portalId, userId, joinBeta, rolloutId, onClose, handleSuccessfulJoin]);
  function getProductTypeKey() {
    if (agentType) {
      return agentType === 'ASSISTANT' ? 'ASSISTANT' : 'AGENT';
    }
    if (productType === 'WORKFLOW_TEMPLATE') {
      return 'WORKFLOW_TEMPLATE';
    }
    return 'DEFAULT';
  }
  return /*#__PURE__*/_jsxs(Modal, {
    use: "success",
    inlineSize: "auto",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: agentHubBeta ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.agentHubBetaHeader"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.joinBetaModal.header.${getProductTypeKey()}`
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [agentHubBeta && /*#__PURE__*/_jsx(Alert, {
        marginBlockEnd: "200",
        titleText: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.agentHubBetaAlert.title'),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.agentHubBetaAlert.body"
        })
      }), /*#__PURE__*/_jsx(Paragraph, {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.description",
          options: {
            betaName: title
          }
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        marginBlock: "500",
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: isTermsAcknowledged,
          onChange: ({
            target: {
              checked
            }
          }) => {
            if (checked) {
              trackInteraction({
                action: 'acknowledge beta terms',
                section: 'join public beta modal'
              });
            }
            setIsTermsAcknowledged(checked);
          },
          children: /*#__PURE__*/_jsx(Text, {
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "detailsPage.productDetailsCard.cta.joinBetaModal.legalAcknowledgement_jsx",
              options: {
                href: getHubSpotBetaTermsUrl(getLang()),
                external: true
              },
              elements: {
                Link
              }
            })
          })
        })
      }), /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx("b", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "detailsPage.productDetailsCard.cta.joinBetaModal.enrollmentNote"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "join-beta-modal-accept-btn",
        disabled: !isTermsAcknowledged,
        failed: Boolean(error),
        loading: loading,
        onClick: handleJoin,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.joinBetaAndAdd.${getProductTypeKey()}`
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.noThanks"
        })
      })]
    })]
  });
};
export { JoinPublicBetaModal };